import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import { Button, FloatingActionBar, Input } from "@chef/components";
import { BRAND_PATHS, FEATURE_FLAGS } from "@chef/constants";
import {
  openModal,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import { Search } from "@chef/icons/small";
import { FILTER_CATEGORIES } from "@chef/constants";
import {
  filterAndSortProductsByCategoriesWithDate,
  getBasketDataFromCalendar,
  getBasketFinances,
  getStandaloneProductCategoryIdsFromProductCategories,
} from "@chef/state-management/helpers";
import { calculateOrderPoints, getCutoffDateForWeek } from "@chef/helpers";
import {
  useDebounce,
  useEffectOnce,
  useIntercom,
  usePrevious,
} from "@chef/hooks";
import { isEmptyArray } from "@chef/utils/array";
import {
  ErrorComponent,
  ProductsFromCategoriesCardGrid,
  CurrencyWrapper as Currency,
  PageLoader,
  BrandSpecificCoin,
  BackToTopButton,
  FilterNavigation,
} from "@chef/smart-components";
import { AppShell } from "@chef/feature-app-shell";
import { useFlag } from "@chef/feature-flags";
import { useLoyalty } from "@chef/state-management/hooks";
import {
  ContentComponent,
  ISection,
  useSectionDataByPathConstraint,
} from "@chef/feature-sanity";
import {
  useAppDispatch,
  useCalendarData,
  useFilters,
  useStandaloneBasket,
  useTrack,
} from "@chef/state-management/hooks";

import { useCreateDeviationBasket } from "../hooks";
import {
  filterProductsByCategoriesFilterCategory,
  generateFilterCategoriesFromProductsByCategories,
  getSteps,
  getTotalProductCount,
} from "../utils";
import { WeekStatus } from "../lib/WeekStatus";

import { intl } from "./WeekEditorStandalone.Intl";

interface WeekEditorStandaloneProps {
  week: number;
  year: number;
  section: ISection | null;
  whyDeliveryChangeSection: ISection | null;
}

export const WeekEditorStandalone = ({
  week,
  year,
  section,
  whyDeliveryChangeSection,
}: WeekEditorStandaloneProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const track = useTrack();
  const { updateUrl } = useFilters();
  const { data: loyaltyData } = useLoyalty();
  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const [week_editor_standalone_top] = useSectionDataByPathConstraint(section);

  useIntercom(false);

  // queries
  const { isLoading: isLoadingCreateDeviationBasket } =
    useCreateDeviationBasket({ week, year });

  const {
    data: productCategoriesData,
    isError: isErrorProductCategories,
    isLoading: isLoadingProductCategories,
  } = useProductCategoriesQuery();

  const {
    data: standaloneProductsByCategoriesData,
    isError: isErrorStandaloneProductsByCategories,
    isLoading: isLoadingStandaloneProductsByCategories,
  } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategoriesData?.productCategories || [],
      ),
      week,
      year,
    },
    { skip: !productCategoriesData },
  );

  const { calendar, isLoading: isLoadingCalendar } = useCalendarData({
    week,
    year,
  });

  const calendarData = calendar?.filter(
    (c) => c.week === week && c.year === year,
  );

  const { basket: selectedProducts } = useStandaloneBasket({
    variant: "deviation",
    week,
    year,
  });

  // misc
  const filteredStandaloneProductsByCategories = useMemo(() => {
    if (!productCategoriesData || !standaloneProductsByCategoriesData) {
      return [];
    }

    return filterAndSortProductsByCategoriesWithDate({
      productsByCategories:
        standaloneProductsByCategoriesData.productsByCategories,
      productCategories: productCategoriesData.productCategories,
      cutoffDate: getCutoffDateForWeek({ week, year }),
    });
  }, [standaloneProductsByCategoriesData, productCategoriesData, week, year]);

  const filtersWithDiscount = useMemo(() => {
    return generateFilterCategoriesFromProductsByCategories({
      productsByCategories: filteredStandaloneProductsByCategories,
    });
  }, [filteredStandaloneProductsByCategories]);

  const currentFilter =
    (router.query?.["filter"] as string) ||
    FILTER_CATEGORIES.ALL_CATEGORIES_SLUG;

  // states
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const prevDebouncedSearchValue = usePrevious(debouncedSearchValue);
  useEffect(() => {
    if (
      debouncedSearchValue.length > 0 &&
      prevDebouncedSearchValue !== debouncedSearchValue
    ) {
      track("Product Searched", {
        affiliation: "Frontend process",
        query: debouncedSearchValue,
      });
    }
  }, [debouncedSearchValue, track, prevDebouncedSearchValue]);

  const steps = getSteps();

  const isError =
    isErrorProductCategories || isErrorStandaloneProductsByCategories;

  const isLoading =
    router.isFallback ||
    isLoadingCalendar ||
    isLoadingStandaloneProductsByCategories ||
    isLoadingProductCategories ||
    isLoadingCreateDeviationBasket;

  useEffectOnce(() => {
    track("deviationUpdateStepViewed", {
      affiliation: "Frontend process",
      delivery_update_step: 2,
      delivery_update_step_name: "userExtra",
      delivery_update_step_title: intl.HEADER,
    });
  }, !isLoading && !isError);

  const handleButtonClick = () => {
    track("deviationUpdateStepCompleted", {
      affiliation: "Frontend process",
      delivery_update_step: 2,
      delivery_update_step_name: "userExtra",
      delivery_update_step_title: intl.HEADER,
    });
  };

  const onCartClick = () => {
    dispatch(openModal({ id: "DEVIATION_CART", week, year }));
  };

  if (!calendarData) {
    return <PageLoader />;
  }

  const basketDataFromCalendar = getBasketDataFromCalendar({
    calendar: calendarData,
    week,
    year,
  })?.mealboxBasket;

  if (isError) {
    return <ErrorComponent />;
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (!basketDataFromCalendar || !loyaltyData) {
    return <PageLoader />;
  }

  const { shippingDetails } = basketDataFromCalendar;

  const totalBasketItemCount = getTotalProductCount(selectedProducts || []);

  const price = getBasketFinances({
    basketProducts: selectedProducts || [],
    customerFee: shippingDetails.plannedDeliveryInformation.customerFee,
    loyaltyLevel: loyaltyData?.currentLevel,
  }).total;

  const filteredProducts = filterProductsByCategoriesFilterCategory({
    filter: currentFilter,
    productsByCategories: filteredStandaloneProductsByCategories,
    searchValue: debouncedSearchValue,
  });

  const shouldShowSection =
    week_editor_standalone_top &&
    router.asPath.includes(week_editor_standalone_top.pathConstraint || "");

  const orderPoints = calculateOrderPoints({
    totalSum: price,
    multiplier: loyaltyData.multiplier,
  });

  const allCount = filtersWithDiscount.find((i) => i.isAll)?.count;

  return (
    <AppShell title={intl.HEADER}>
      <AppShell.Meta />

      <AppShell.Drawer />

      {isWeekEditorRedesignEnabled ? (
        <>
          <AppShell.Sticky anchor="top" float>
            <AppShell.Header variant="my-menu" />
          </AppShell.Sticky>
          <WeekStatus
            week={week}
            year={year}
            sanityData={whyDeliveryChangeSection || null}
          />
        </>
      ) : (
        <AppShell.Sticky anchor="top">
          <AppShell.Header
            variant="steps"
            steps={steps}
            current={2}
            backHref={BRAND_PATHS.WEEK_EDITOR_PAGE({ week, year })}
            exitLabel={intl.BACK}
            cartCount={totalBasketItemCount}
            onCartClick={onCartClick}
          />
        </AppShell.Sticky>
      )}

      <AppShell.Main>
        {shouldShowSection && (
          <ContentComponent block={week_editor_standalone_top.content} />
        )}
        <h1 className="mt-6 mb-4 text-xl md:text-3xl ">
          <strong>{intl.GROCERIES}</strong>
        </h1>

        <div className="mb-8 md:w-80">
          <Input
            type="search"
            Icon={Search}
            autoComplete="off"
            name="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={
              allCount ? intl.SEARCH_AMONG_X_ITEMS(allCount) : intl.SEARCH
            }
          />
        </div>

        <div className="flex flex-col" id="special-items-grid">
          {!debouncedSearchValue && (
            <FilterNavigation
              items={filtersWithDiscount}
              value={currentFilter}
              onChange={updateUrl}
              resetFilters={() =>
                updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)
              }
              className="mb-8 lg:mb-12"
              leftClassName="bg-gradient-to-r from-background via-background"
              rightClassName="bg-gradient-to-l from-background via-background"
            />
          )}

          {isEmptyArray(filteredProducts.flatMap((c) => c.products))
            ? intl.COULD_NOT_FIND_ANY
            : filteredProducts.map((category) => {
                if (category.products.length === 0) {
                  return null;
                }

                return (
                  <div
                    key={category.productCategoryId}
                    id={category.productCategoryId}
                  >
                    <h2 className="text-lg">
                      <strong>{category.productCategoryName}</strong>
                    </h2>
                    <ProductsFromCategoriesCardGrid
                      products={category.products}
                      categoryId={category.productCategoryId}
                      view="deviation"
                      basketType="deviation"
                      week={week}
                      year={year}
                    />
                  </div>
                );
              })}
        </div>

        <BackToTopButton />
      </AppShell.Main>

      {isWeekEditorRedesignEnabled ? (
        <AppShell.Sticky anchor="bottom">
          <AppShell.TabBar variant="my-menu" />
        </AppShell.Sticky>
      ) : (
        <FloatingActionBar hasPadding={false} className="p-4">
          <div className="flex flex-row justify-between col-span-full">
            <div className="flex flex-col col-span-full" id="total-price">
              <p className="text-sm underline">
                <b>{intl.TOTAL}</b>
              </p>

              <div className="flex items-center">
                <Currency>{price}</Currency>

                <BrandSpecificCoin className="ml-2" />

                <p className="ml-1">
                  <b>
                    {orderPoints} {intl.POINTS}
                  </b>
                </p>
              </div>
            </div>
            <Link
              passHref
              href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year })}
              onClick={handleButtonClick}
            >
              <Button
                primary
                className="self-center col-span-3 col-start-10 no-underline"
                id="floating-editor-bar-btn"
              >
                {intl.NEXT_BUTTON}
              </Button>
            </Link>
          </div>
        </FloatingActionBar>
      )}
    </AppShell>
  );
};
